import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ScanText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ocrApi, type OcrReceipt } from '@/api/ocr.api';
import { useCargar } from '@/hooks/useCasino';
import { formatCurrency } from '@/lib/utils';

/**
 * Botón "Leer OCR" bajo un comprobante: extrae monto/fecha/referencia con el
 * engine OCR (gpt-4o-mini Vision) y ofrece cargar ese monto al jugador en un click.
 */
export function ReceiptOcrButton({ imageUrl, clientId }: { imageUrl: string; clientId: string }) {
  const [result, setResult] = useState<OcrReceipt | null>(null);
  const cargar = useCargar();

  const ocr = useMutation({
    mutationFn: () => ocrApi.receipt(imageUrl),
    onSuccess: (res) => {
      if (res?.amount) setResult(res);
      else toast.error('OCR no pudo leer el monto del comprobante');
    },
    onError: () => toast.error('Error leyendo el comprobante'),
  });

  const amountNum = result?.amount ? Number(String(result.amount).replace(/[.,](?=\d{3}\b)/g, '').replace(',', '.')) : 0;

  const cargarMonto = async () => {
    if (!amountNum || amountNum <= 0) {
      toast.error('Monto inválido');
      return;
    }
    try {
      await cargar.mutateAsync({ clientId, amount: amountNum });
      setResult(null);
    } catch {
      /* el hook ya muestra el toast */
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="mt-1 h-7 gap-1 text-xs"
        disabled={ocr.isPending}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          ocr.mutate();
        }}
      >
        {ocr.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <ScanText className="h-3 w-3" />}
        Leer OCR
      </Button>

      <Dialog open={result !== null} onOpenChange={(o) => !o && setResult(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>📄 Comprobante leído</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Monto</span>
              <span className="font-bold">
                {amountNum ? formatCurrency(amountNum, result?.currency || 'PYG') : '—'}
              </span>
            </div>
            {result?.fecha && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Fecha</span>
                <span>{result.fecha}</span>
              </div>
            )}
            {result?.referencia && (
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground">Referencia</span>
                <span className="font-mono text-xs break-all">{result.referencia}</span>
              </div>
            )}
            {result?.destinatario && (
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground">Destinatario</span>
                <span className="text-xs">{result.destinatario}</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setResult(null)}>
              Cerrar
            </Button>
            <Button size="sm" onClick={() => void cargarMonto()} disabled={cargar.isPending || !amountNum}>
              {cargar.isPending ? 'Cargando…' : `Cargar ${amountNum ? amountNum.toLocaleString('es-AR') : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
